import { refQueryName } from '@/features/documents/nodes/resolve'
import { messages } from '@/lib/messages'
import type { DocNode } from '@/lib/report-document'

import type { DocumentTemplate } from './serialize'

const t = messages.documents.editor.refs

export interface RefOption {
  value: string
  label: string
  group: string
}

export type RefKind = 'value' | 'change' | 'left' | 'right'

/**
 * Ayar panelindeki başvuru seçenekleri (contracts.md §12 kuralları): sorgu üyeleri,
 * `repeat` içinde `row.` üyeleri, yalnız `value` için `period.label` / `filters.<üye>`,
 * `change` ve `prev.` yalnız `compare: "previous"` sorgularında, `prev.` yalnız koşulun sağında.
 */
export function refOptions(
  template: DocumentTemplate,
  kind: RefKind,
  repeatOver?: string,
): RefOption[] {
  const options: RefOption[] = []
  const members = (name: string) => {
    const query = template.queries[name]
    return query ? [...query.measures, ...query.dimensions] : []
  }
  const comparable = (name: string) => template.queries[name]?.compare === 'previous'

  if (kind === 'value') {
    if (template.filters.some((filter) => filter.type === 'period')) {
      options.push({ value: 'period.label', label: t.periodLabel, group: t.period })
    }
    for (const filter of template.filters) {
      if (filter.type === 'multiSelect' || filter.type === 'boolean') {
        options.push({
          value: `filters.${filter.member}`,
          label: t.filter(filter.member),
          group: t.filters,
        })
      }
    }
  }
  if (repeatOver && template.queries[repeatOver] && (kind !== 'change' || comparable(repeatOver))) {
    for (const member of members(repeatOver)) {
      options.push({ value: `row.${member}`, label: member, group: t.row })
    }
  }
  for (const name of Object.keys(template.queries)) {
    if (kind === 'change' && !comparable(name)) continue
    for (const member of members(name)) {
      options.push({ value: `${name}.${member}`, label: member, group: name })
    }
  }
  if (kind === 'right') {
    for (const name of Object.keys(template.queries)) {
      if (!comparable(name)) continue
      for (const member of members(name)) {
        options.push({ value: `prev.${name}.${member}`, label: member, group: t.previous(name) })
      }
    }
  }
  return options
}

/** Sorguya yapılan başvuru sayısı (`value`, `change`, `condition`, `repeat`); silmeyi engellemek için. */
export function countQueryUsage(node: DocNode, name: string, repeatOver?: string): number {
  const attrs = node.attrs ?? {}
  let count = 0
  const uses = (ref: unknown) =>
    typeof ref === 'string' && refQueryName(ref, repeatOver) === name ? 1 : 0
  switch (node.type) {
    case 'value':
    case 'change':
      count += uses(attrs.of)
      break
    case 'condition':
      count += uses(attrs.left) + uses(attrs.right)
      break
    case 'repeat':
      count += attrs.over === name ? 1 : 0
      repeatOver = attrs.over as string
      break
  }
  for (const child of node.content ?? []) count += countQueryUsage(child, name, repeatOver)
  return count
}
